// Shared UI primitives. Owned by the spec; both frontend agents use these instead of re-inventing them.
// Add new shared primitives in web/src/components/ui-extra.tsx (FRONTEND-DIALER owns that file).
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';

export function Modal(props: { title: string; onClose: () => void; children: ComponentChildren; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') props.onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div style="position:fixed;inset:0;background:rgba(28,27,26,.45);display:flex;align-items:flex-start;justify-content:center;padding:6vh 16px;z-index:50"
      onClick={(e) => { if (e.target === e.currentTarget) props.onClose(); }}>
      <div class="card" style={`width:100%;max-width:${props.width ?? 560}px;max-height:86vh;overflow:auto`} role="dialog" aria-label={props.title}>
        <div class="row" style="justify-content:space-between;margin-bottom:12px">
          <h2 style="margin:0">{props.title}</h2>
          <button class="btn btn-sm" onClick={props.onClose} aria-label="Close">Esc</button>
        </div>
        {props.children}
      </div>
    </div>
  );
}

type ToastKind = 'ok' | 'warn' | 'bad' | 'info';
let pushToast: ((t: { kind: ToastKind; text: string }) => void) | null = null;
export function toast(text: string, kind: ToastKind = 'info'): void { pushToast?.({ kind, text }); }

/** Mount once (the FRONTEND-DIALER agent mounts it in the app shell). */
export function Toasts() {
  const [items, setItems] = useState<Array<{ id: number; kind: ToastKind; text: string }>>([]);
  useEffect(() => {
    let n = 0;
    pushToast = (t) => {
      const id = ++n;
      setItems((xs) => [...xs, { id, ...t }]);
      setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), t.kind === 'bad' ? 8000 : 4000);
    };
    return () => { pushToast = null; };
  }, []);
  return (
    <div style="position:fixed;right:16px;top:16px;display:flex;flex-direction:column;align-items:flex-end;gap:8px;z-index:60;pointer-events:none;max-width:min(420px,calc(100vw - 32px))">
      {items.map((t) => <div key={t.id} class={`chip chip-${t.kind}`} style="font-size:14px;padding:10px 14px;box-shadow:var(--shadow-hover);pointer-events:auto;white-space:normal">{t.text}</div>)}
    </div>
  );
}

export function ErrorBanner(props: { error: string | null }) {
  if (!props.error) return null;
  return <div class="chip chip-bad" style="display:block;padding:10px 12px;margin:8px 0;font-size:14px">{props.error}</div>;
}

export function Empty(props: { children: ComponentChildren }) {
  return <div class="card muted" style="text-align:center;padding:28px">{props.children}</div>;
}

export function fmtDateTime(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: tz ? 'short' : undefined });
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export function pct(x: number | null | undefined, digits = 1): string {
  return x === null || x === undefined ? '—' : `${(x * 100).toFixed(digits)}%`;
}
